<template>
    <!-- a table list sits in the merchant portal Orders card: the filter bar on top, then the table
         (tasks#211) -->
    <div
        :class="[
            'admin-data-iterator',
            { 'dd-table-card': framed, 'dd-table-inline': inline },
        ]"
    >
        <div
            v-if="hasSlot('filters') || hasSlot('actions')"
            class="dd-table-card__head"
        >
            <div class="dd-table-card__filters">
                <slot
                    name="filters"
                    :items="items"
                    :busy="busy"
                    :total="total"
                    :query="filters"
                ></slot>
            </div>
            <div class="dd-iterator-actions">
                <slot
                    name="actions"
                    :items="items"
                    :busy="busy"
                    :total="total"
                    :query="filters"
                ></slot>
            </div>
        </div>

        <skeleton-loader
            v-if="busy && items.length === 0"
            :type="skeleton"
        ></skeleton-loader>
        <overlay
            v-show="!(busy && items.length === 0)"
            :show="(busy && items.length > 0) || showOverlay"
            :color="overlayColor"
        >
            <slot name="before"></slot>
            <slot
                :items="items"
                :busy="busy"
                :total="total"
                :query="filters"
            ></slot>
            <div
                v-if="items.length === 0 && !busy && $slots.noItems"
                class="text-center py-8 text-caption font-italic g-73--text"
            >
                <slot name="noItems"></slot>
            </div>

            <portal to="footer">
                <slot
                    name="batchActions"
                    :items="items"
                    :busy="busy"
                    :total="total"
                    :query="filters"
                ></slot>
            </portal>
            <!-- the sticky app footer carries the list's paging: "1–20 of 64 results", the pager
                 and the page size -->
            <portal
                v-if="!disablePagination && !paginatorBelowTable && !inline"
                to="footer-right"
            >
                <div class="dd-table-footer">
                    <span v-if="framed" class="dd-table-footer__count">{{
                        resultsText
                    }}</span>
                    <per-page-menu
                        v-if="fixedItemsPerPage == null"
                        v-model="itemsPerPage"
                        :choices="perPageChoices"
                    ></per-page-menu>
                    <div
                        v-if="showPager"
                        class="dd-pager"
                        :class="{ 'dd-pager--wide': length > 3 }"
                    >
                        <v-pagination
                            :key="length > 3 ? 'wide' : 'narrow'"
                            v-model="page"
                            :length="length"
                            total-visible="7"
                            color="primary"
                        ></v-pagination>
                    </div>
                </div>
            </portal>
            <div
                v-else-if="
                    !disablePagination &&
                    (paginatorBelowTable || inline) &&
                    total > itemsPerPage
                "
                class="dd-table-footer my-2"
            >
                <per-page-menu
                    v-if="fixedItemsPerPage == null"
                    v-model="itemsPerPage"
                    :choices="perPageChoices"
                ></per-page-menu>
                <div
                    v-if="showPager"
                    class="dd-pager"
                    :class="{ 'dd-pager--wide': length > 3 }"
                >
                    <v-pagination
                        :key="length > 3 ? 'wide' : 'narrow'"
                        v-model="page"
                        :length="length"
                        total-visible="7"
                        color="primary"
                    ></v-pagination>
                </div>
            </div>

            <slot name="after"></slot>
        </overlay>
    </div>
</template>

<script>
import SkeletonLoader from '@/components/elements/SkeletonLoader'
import isEqual from 'lodash/isEqual'
import mergeWith from 'lodash/mergeWith'
import cloneDeep from 'lodash/cloneDeep'
import isPlainObject from 'lodash/isPlainObject'
import { DEFAULT_PERPAGE, PERPAGE_CHOICES } from '@/defaults'
import RestApiCollection from '@/api/RestApiCollection'
import Overlay from '@/components/overlay/Overlay'
import PerPageMenu from './PerPageMenu'
import MapperCollection from '@/services/mapper/MapperCollection'
import { EventBus } from '@/plugins/events'
import axios from 'axios'
import Notification from '@/services/Notification/Notification'

const TimeoutThrottleS = 70

export default {
    components: {
        Overlay,
        SkeletonLoader,
        PerPageMenu,
    },
    props: {
        api: {
            type: String,
            required: true,
        },
        filters: {
            type: Object,
            default: () => {},
        },
        fixedFilters: {
            type: Object,
            default: () => {
                return {}
            },
        },
        disableQueryParams: {
            type: Boolean,
        },
        fixedItemsPerPage: {
            type: Number,
            default: null,
        },
        disablePagination: {
            type: Boolean,
            default: false,
        },
        mapper: {
            type: String,
            default: null,
        },
        showOverlay: {
            type: Boolean,
            default: false,
        },
        overlayColor: {
            type: String,
            default: undefined,
        },
        paginatorBelowTable: Boolean,
        // a table inside a form panel: no card of its own, the paging under the table
        inline: Boolean,
        // SkeletonLoader type shown on the first load
        skeleton: {
            type: String,
            default: 'table',
        },
    },
    data() {
        return {
            total: 0,
            page: 1,
            busy: false,
            items: [],
            sentQuery: {},
            updateItemsTimeout: null,
            loadedOnce: false,
            itemsPerPage: 50,
            abortController: null,
            requestTimedOut: null,
        }
    },
    computed: {
        perPageChoices() {
            return PERPAGE_CHOICES
        },
        length() {
            return Math.ceil(this.total / this.itemsPerPage)
        },
        // table lists get the Orders card; a card list (skeleton "cards") and a table inside a
        // form panel (inline) stay unframed
        framed() {
            return this.skeleton === 'table' && !this.inline
        },
        showPager() {
            return (
                this.fixedItemsPerPage == null ||
                Math.ceil(this.total / this.itemsPerPage) > 1
            )
        },
        // "1–20 of 64 results"
        resultsText() {
            const total = this.total || 0
            const from = total ? (this.page - 1) * this.itemsPerPage + 1 : 0
            const to = Math.min(this.page * this.itemsPerPage, total)
            return this.$te('labels.results_range')
                ? this.$t('labels.results_range', { from, to, total })
                : `${from}–${to} of ${total} results`
        },
    },
    watch: {
        filters: {
            deep: true,
            immediate: true,
            handler(newFilters, oldFilters) {
                if (isEqual(newFilters, oldFilters)) return
                if (
                    newFilters?.page === oldFilters?.page &&
                    newFilters?.page !== 1
                )
                    this.page = 1

                this.updateItems()
            },
        },
        itemsPerPage(value) {
            this.$emit('update:filters', {
                ...this.filters,
                ...{ itemsPerPage: value },
            })
        },
        page(value) {
            this.$emit('update:filters', {
                ...this.filters,
                ...{ page: value },
            })
        },
    },
    created() {
        EventBus.$on('updateList', this.updateList)

        this.itemsPerPage = DEFAULT_PERPAGE
        if (
            !this.disableQueryParams &&
            !isEqual(this.$route.query, {
                ...this.filters,
                ...{
                    page: 1,
                    itemsPerPage: DEFAULT_PERPAGE,
                },
            })
        ) {
            const query = {
                ...this.filters,
                ...{
                    page: 1,
                    itemsPerPage: DEFAULT_PERPAGE,
                },
                ...this.$route.query,
            }

            this.page = parseInt(query.page)
            this.itemsPerPage = parseInt(query.itemsPerPage)
            this.$emit('update:filters', query)
        }

        if (this.fixedItemsPerPage !== null) {
            this.itemsPerPage = this.fixedItemsPerPage
        }
    },
    beforeDestroy() {
        EventBus.$off('updateList', this.updateList)
    },
    methods: {
        hasSlot(name) {
            return !!this.$slots[name] || !!this.$scopedSlots[name]
        },
        mergeData(target, source) {
            return mergeWith({}, target, source, (objValue, srcValue, key) => {
                if (!(key in target)) {
                    return objValue
                }

                if (Array.isArray(srcValue)) {
                    return srcValue
                }

                if (isPlainObject(objValue) && isPlainObject(srcValue)) {
                    return this.mergeData(objValue, srcValue)
                }

                return srcValue
            })
        },
        updateItem(data) {
            if (!Array.isArray(this.items) || !data?.id) return
            const index = this.items.findIndex((item) => item.id === data.id)
            if (index === -1) return

            this.$set(
                this.items,
                index,
                this.mergeData(cloneDeep(this.items[index]), data)
            )
        },
        updateList(api) {
            if (api === this.api) this.updateItems(true)
        },
        updateItems(update = false, onlyReload = false) {
            return new Promise((resolve) => {
                const query = { ...this.filters, ...this.fixedFilters }

                if (
                    isEqual(query, this.sentQuery) &&
                    !update &&
                    this.items.length
                ) {
                    resolve()
                    return
                }

                this.busy = true
                this.sentQuery = query

                if (this.updateItemsTimeout !== null) {
                    clearTimeout(this.updateItemsTimeout)
                }

                // Cancel previous request
                if (this.abortController) {
                    this.abortController.abort()
                }

                this.abortController = new AbortController()

                this.updateItemsTimeout = setTimeout(
                    () => {
                        if (
                            !this.disableQueryParams &&
                            !onlyReload &&
                            !isEqual(this.$route.query, this.filters)
                        ) {
                            // console.log(this.$route.query, this.filters)
                            this.$router.replace({
                                query: this.filters,
                            })
                        }

                        if (this.requestTimedOut) {
                            const now = this.$date()
                            const throttleEnd = this.requestTimedOut.add(
                                TimeoutThrottleS,
                                's'
                            )
                            if (now.isBefore(throttleEnd)) {
                                this.busy = false
                                Notification.error(
                                    this.$t(
                                        'notifications.timeout_reload_error.title'
                                    ),
                                    this.$t(
                                        'notifications.timeout_reload_error.message',
                                        {
                                            timeremaining: throttleEnd.diff(
                                                now,
                                                's'
                                            ),
                                        }
                                    )
                                )
                                resolve()
                                return
                            } else {
                                this.requestTimedOut = null
                            }
                        }

                        RestApiCollection.get(this.api)
                            .list(query, {
                                signal: this.abortController.signal,
                            })
                            .then((data) => {
                                if (data instanceof axios.Cancel) {
                                    throw { name: 'AbortError' }
                                }
                                this.items = this.mapper
                                    ? MapperCollection.get(this.mapper).map(
                                          data['hydra:member']
                                      )
                                    : data['hydra:member']
                                this.total = data['hydra:totalItems']
                                this.$emit('items-updated')
                            })
                            .catch((err) => {
                                if (err.name === 'AbortError') {
                                    resolve()
                                    return
                                }

                                if (err.code === 'ECONNABORTED') {
                                    this.requestTimedOut = this.$date()
                                }
                            })
                            .finally(() => {
                                resolve()
                                this.busy = false
                                this.loadedOnce = true
                            })
                        // the first load does not wait: the filter updates of mounting (defaults, the
                        // route query) land before the next task anyway; later changes (typing in a
                        // search) are still bundled for 100 ms (tasks#211)
                    },
                    this.loadedOnce ? 100 : 0
                )
            })
        },
    },
}
</script>

<style lang="scss">
.admin-data-iterator {
    // a list without actions (orders) keeps no empty 24px row under its filters on phones
    .dd-iterator-actions:empty {
        display: none;
    }

    // a reorderable side list (option groups, categories) is a DoorDash settings list: a bordered
    // card, 48px rows divided by #e7e7e7, the selected row #f1f1f1 and bold (tasks#211)
    &.dandd-iterator {
        > .v-list,
        .v-list {
            padding: 0;
            border: 1px solid #e7e7e7;
            border-radius: 12px;
            overflow: hidden;
        }

        .v-list-item {
            min-height: 48px;
            font-size: 14px;

            & + .v-list-item {
                border-top: 1px solid #e7e7e7;
            }

            h2 {
                margin: 0 !important;
                font-size: 18px;
                line-height: 24px;
            }
        }

        .v-list-item.active {
            background-color: #f1f1f1;
            font-weight: 700;
        }
    }
}
</style>
